<script setup>
import { inject, onMounted, onUnmounted, ref } from 'vue';
import { TabPanel } from '@headlessui/vue';
import { trans } from 'laravel-vue-i18n';
import PhotoSwipeLightbox from 'photoswipe/lightbox';
import 'photoswipe/style.css';

const programPackage = inject('programPackage');

const gallery = ref(null);
const lightbox = ref();

onMounted(() => {
    if (!lightbox.value) {
        lightbox.value = new PhotoSwipeLightbox({
            gallery: gallery.value,
            children: 'a',
            closeTitle: trans('components.photoswipe.closeTitle'),
            arrowPrevTitle: trans('components.photoswipe.arrowPrevTitle'),
            arrowNextTitle: trans('components.photoswipe.arrowNextTitle'),
            pswpModule: () => import('photoswipe'),
            preloadFirstSlide: false,
        });

        lightbox.value.on('uiRegister', () => {
            lightbox.value.pswp.ui.registerElement({
                name: 'custom-caption',
                order: 9,
                isButton: false,
                appendTo: 'root',
                html: null,
                onInit(element, pswp) {
                    pswp.on('change', () => {
                        const currentSlideElement = pswp.currSlide.data.element;

                        if (currentSlideElement) {
                            const hiddenCaption = currentSlideElement.querySelector('.caption-content');

                            element.innerHTML = hiddenCaption ? hiddenCaption.innerHTML || '' : null;
                        }
                    });
                },
            });
        });

        lightbox.value.init();
    }
});

onUnmounted(() => {
    if (lightbox.value) {
        lightbox.value.destroy();
        lightbox.value = null;
    }
});
</script>

<template>
    <TabPanel class="focus-visible:outline-deep-teal-700">
        <h2 class="text-2xl font-semibold text-deep-teal-700 pb-4">
            {{ $t('eventDetails.tabs.review') }}
        </h2>

        <div
            ref="gallery"
            class="space-y-8"
        >
            <ul
                v-if="programPackage.event_container.review.images?.length > 0"
                role="list"
                class="grid grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-6 md:gap-12"
            >
                <li
                    v-for="(image, index) in programPackage.event_container.review.images"
                    :key="index"
                    class="flex"
                >
                    <a
                        :href="image.src"
                        :data-pswp-width="image.width"
                        :data-pswp-height="image.height"
                        :data-cropped="true"
                        target="_blank"
                        class="contents"
                    >
                        <figure class="contents">
                            <img
                                :src="image.thumbnail"
                                class="object-cover ring-1 ring-gray-200 hover:ring-gray-700"
                                :alt="image.alt"
                                loading="lazy"
                                :aria-label="image.creditLine ? `${$t('components.creditLine.prefix')} ${image.creditLine}` : null"
                            >

                            <figcaption
                                v-if="image.creditLine"
                                class="hidden caption-content"
                            >
                                <div class="px-4 py-0.5">
                                    {{ $t('components.creditLine.prefix') }} {{ image.creditLine }}
                                </div>
                            </figcaption>
                        </figure>
                    </a>
                </li>
            </ul>

            <!-- eslint-disable vue/no-v-html -->
            <div
                v-if="programPackage.event_container.review.text"
                class="prose 2xl:prose-lg"
                v-html="programPackage.event_container.review.text"
            />
            <!-- eslint-enable vue/no-v-html -->
        </div>
    </TabPanel>
</template>

<style>
.pswp__custom-caption {
    @apply absolute left-1/2 bottom-4 -translate-x-1/2 text-sm/tight text-white bg-black/70 rounded-lg;
}
</style>
